import React, { useState } from 'react';
import Loading from '../components/Loading';
import ZodiacSelector from '../components/ZodiacSelector';
import { getCompatibility, getGunaCompatibility, isGeminiReady } from '../services/geminiService';
import { useLanguage } from '../context/LanguageContext';
import ShareReading from '../components/ShareReading';
import ReadingDisclaimer from '../components/ReadingDisclaimer';

export default function Compatibility() {
  const [sign1, setSign1] = useState('');
  const [sign2, setSign2] = useState('');
  const [name1, setName1] = useState('');
  const [name2, setName2] = useState('');
  const [dob1, setDob1] = useState('');
  const [dob2, setDob2] = useState('');
  const [selectingFor, setSelectingFor] = useState(1);
  const [compatType, setCompatType] = useState('western'); // 'western' or 'guna'
  const [reading, setReading] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { language, t } = useLanguage();

  const handleSelect = (sign) => {
    if (selectingFor === 1) {
      setSign1(sign);
      setSelectingFor(2);
    } else {
      setSign2(sign);
    }
  };

  const handleCheck = async () => {
    if (!sign1 || !sign2) {
      setError(t('selectBothSigns'));
      return;
    }
    if (!isGeminiReady()) {
      setError(t('apiKeyError'));
      return;
    }
    setLoading(true);
    setError('');
    setReading('');
    try {
      const result = compatType === 'guna'
        ? await getGunaCompatibility(sign1, sign2, name1, name2, dob1, dob2, language)
        : await getCompatibility(sign1, sign2, name1, name2, language);
      setReading(result);
    } catch (err) {
      setError(err.message || 'Failed to check compatibility.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page">
      <div className="container">
        <div className="page-header">
          <h2>{t('compatibilityTitle')}</h2>
          <p>{t('compatibilityDesc')}</p>
        </div>

        {/* Compatibility Type Toggle */}
        <div className="tabs" style={{ marginBottom: '2rem' }}>
          <button
            className={`tab ${compatType === 'western' ? 'active' : ''}`}
            onClick={() => setCompatType('western')}
          >
            💕 Western Compatibility
          </button>
          <button
            className={`tab ${compatType === 'guna' ? 'active' : ''}`}
            onClick={() => setCompatType('guna')}
          >
            🕉️ Ashtakoot Guna Milan
          </button>
        </div>

        {/* Name inputs */}
        <div className="card" style={{ maxWidth: '600px', margin: '0 auto 2rem' }}>
          <div className="form-row">
            <div className="form-group">
              <label>{t('person1Name')}</label>
              <input type="text" value={name1} onChange={e => setName1(e.target.value)} placeholder={t('firstPersonName')} />
            </div>
            <div className="form-group">
              <label>{t('person2Name')}</label>
              <input type="text" value={name2} onChange={e => setName2(e.target.value)} placeholder={t('secondPersonName')} />
            </div>
          </div>

          {/* DOB inputs for Guna Milan */}
          {compatType === 'guna' && (
            <div className="form-row" style={{ marginTop: '1rem' }}>
              <div className="form-group">
                <label>Person 1 Date of Birth</label>
                <input type="date" value={dob1} onChange={e => setDob1(e.target.value)} />
              </div>
              <div className="form-group">
                <label>Person 2 Date of Birth</label>
                <input type="date" value={dob2} onChange={e => setDob2(e.target.value)} />
              </div>
            </div>
          )}

          <p style={{ fontSize: '0.8rem', color: '#999', margin: '1rem 0 0', lineHeight: 1.5, textAlign: 'center' }}>
            ⚠️ By entering another person's details, you confirm that you have their consent to share
            their information for this compatibility check.
          </p>

          {/* Selected Signs Display */}
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '2rem', margin: '1.5rem 0' }}>
            <div
              style={{
                textAlign: 'center',
                padding: '1rem 2rem',
                border: `2px ${sign1 ? 'solid' : 'dashed'} ${sign1 ? 'var(--accent-gold)' : 'var(--border-color)'}`,
                borderRadius: 'var(--radius)',
                cursor: 'pointer',
                background: selectingFor === 1 ? 'rgba(255,215,0,0.05)' : 'transparent',
                minWidth: '120px',
              }}
              onClick={() => setSelectingFor(1)}
            >
              <div style={{ fontSize: '2rem' }}>{sign1 ? '♥' : '?'}</div>
              <div style={{ color: 'var(--accent-gold)', fontFamily: 'var(--font-heading)' }}>
                {sign1 || t('selectSign1')}
              </div>
            </div>

            <span style={{ fontSize: '2rem', color: 'var(--accent-pink)' }}>💕</span>

            <div
              style={{
                textAlign: 'center',
                padding: '1rem 2rem',
                border: `2px ${sign2 ? 'solid' : 'dashed'} ${sign2 ? 'var(--accent-gold)' : 'var(--border-color)'}`,
                borderRadius: 'var(--radius)',
                cursor: 'pointer',
                background: selectingFor === 2 ? 'rgba(255,215,0,0.05)' : 'transparent',
                minWidth: '120px',
              }}
              onClick={() => setSelectingFor(2)}
            >
              <div style={{ fontSize: '2rem' }}>{sign2 ? '♥' : '?'}</div>
              <div style={{ color: 'var(--accent-gold)', fontFamily: 'var(--font-heading)' }}>
                {sign2 || t('selectSign2')}
              </div>
            </div>
          </div>

          <p style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: '1rem' }}>
            {selectingFor === 1 ? t('selectingPerson1') : t('selectingPerson2')}
          </p>
        </div>

        {/* Zodiac Selector */}
        <ZodiacSelector selectedSign={selectingFor === 1 ? sign1 : sign2} onSelect={handleSelect} />

        {/* Check Button */}
        {sign1 && sign2 && (
          <div style={{ textAlign: 'center', marginTop: '2rem' }}>
            <button className="btn btn-gold" onClick={handleCheck} disabled={loading}>
              {loading ? t('checkingStars') : (
                compatType === 'guna'
                  ? `🕉️ Calculate Guna Milan for ${sign1} & ${sign2}`
                  : t('checkCompatibility', { sign1, sign2 })
              )}
            </button>
          </div>
        )}

        {loading && <Loading message={t('loadingCompatibility')} />}
        {error && <div className="error-message">{error}</div>}

        {reading && (
          <div className="result-container">
            <div className="result-card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h3>{compatType === 'guna' ? `🕉️ Guna Milan — ${sign1} & ${sign2}` : t('compatibilityResult', { sign1, sign2 })}</h3>
                <ShareReading title={`${sign1} & ${sign2} Compatibility`} content={reading} type="compatibility" />
              </div>
              <div className="content">{reading}</div>
              <ReadingDisclaimer />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
